/**
 * Returns the product edges part of a Shopify Storefront API query
 *
 * Options:
 * - get_description: include product description (default: false)
 * - get_collections: include product collections (default: false)
 * - get_tags: include product tags (default: false)
 * - get_metafields: include detailed product metafields (default: false)
 * - get_inventory: include inventory metafields (default: false)
 * - get_metafields_for_tiles: include basic metafields for product tiles (default: false)
 *
 * Note: make sure to expose the needed metafields to the Storefront API first using an external GraphQL query
 *
 * @param {Object} options
 * @returns {String} GraphQL query part for product edges
 */
export default (options) => {
  let description_fields = '';
  if (options.get_description) {
    description_fields = `description
    descriptionHtml`;
  }

  // Already in Nosto but we still need it for the quickview in order to load the promo message correctly
  let collections_fields = '';
  if (options.get_collections) {
    collections_fields = `collections(first: 20) {
      edges {
        node {
          handle
        }
      }
    }`;
  }

  // Already in Nosto
  let collections_tags = '';
  if (options.get_tags) {
    collections_tags = 'tags';
  }

  // Don't forget to expose the metafield to the storefront API first using an external GraphQL query
  let product_metafields_query = '';
  let variant_metafields_query = '';
  if (options.get_metafields) {
    product_metafields_query += `
      okendoSummaryData: metafield(namespace: "okendo", key: "summaryData") {
        value
      }
      astrBestseller14: metafield(namespace: "astr", key: "bestseller_product_14") {
        value
      }
      astrBestseller30: metafield(namespace: "astr", key: "bestseller_product_30") {
        value
      }
      astrCombinedProduct: metafield(namespace: "astr", key: "combined_product") {
        value
      }
      astrCombinedProductParent: metafield(namespace: "astr", key: "combined_product_parent") {
        value
      }
      astrSwatches: metafield(namespace: "astr", key: "swatches") {
        value
      }
      astrImagesDetails: metafield(namespace: "astr", key: "images_details") {
        value
      }
      astrSizefitHeight: metafield(namespace: "astr", key: "sizefit_height") {
        value
      }
      astrSizefitHip: metafield(namespace: "astr", key: "sizefit_hip") {
        value
      }
      astrSizefitWaist: metafield(namespace: "astr", key: "sizefit_waist") {
        value
      }
      astrSizefitBust: metafield(namespace: "astr", key: "sizefit_bust") {
        value
      }
      astrSizefitFit: metafield(namespace: "astr", key: "sizefit_fit") {
        value
      }
      astrSizefitLength: metafield(namespace: "astr", key: "sizefit_length") {
        value
      }
      astrSizefitSize: metafield(namespace: "astr", key: "sizefit_size") {
        value
      }
      astrSizefitInseam: metafield(namespace: "astr", key: "sizefit_inseam") {
        value
      }
      astrSizefitSizingRecommendation: metafield(namespace: "astr", key: "sizefit_sizing_recommendation") {
        value
      }
      astrDetailcareWashingInstructions: metafield(namespace: "astr", key: "detailcare_washing_instructions") {
        value
      }
      astrDetailcareFabricContent: metafield(namespace: "astr", key: "detailcare_fabric_content") {
        value
      }
      astrDetailcareAttribute1: metafield(namespace: "astr", key: "detailcare_attribute_1") {
        value
      }
      astrDetailcareAttribute2: metafield(namespace: "astr", key: "detailcare_attribute_2") {
        value
      }
      astrDetailcareAttribute3: metafield(namespace: "astr", key: "detailcare_attribute_3") {
        value
      }
      astrDetailcareStyleNumber: metafield(namespace: "astr", key: "detailcare_style_number") {
        value
      }
    `;

    variant_metafields_query = `
      astrStyleDescription: metafield(namespace: "astr", key: "style_description") {
        value
      }
      astrStyleSizefitHeight: metafield(namespace: "astr", key: "style_model_height") {
        value
      }
      astrStyleSizefitSize: metafield(namespace: "astr", key: "style_model_size") {
        value
      }
      astrStyleSizefitHip: metafield(namespace: "astr", key: "style_model_hips") {
        value
      }
      astrStyleSizefitWaist: metafield(namespace: "astr", key: "style_model_waist") {
        value
      }
      astrStyleSizefitBust: metafield(namespace: "astr", key: "style_model_bust") {
        value
      }
      astrStyleSizefitFit: metafield(namespace: "astr", key: "style_sizefit_fits") {
        value
      }
      astrStyleSizefitLength: metafield(namespace: "astr", key: "style_sizefit_length") {
        value
      }
      astrStyleSizefitInseam: metafield(namespace: "astr", key: "style_sizefit_inseam") {
        value
      }
      astrStyleSizefitSizingRecommendation: metafield(namespace: "astr", key: "style_sizefit_sizing_recommendation") {
        value
      }
    `;
  }

  if (options.get_metafields_for_tiles) {
    variant_metafields_query += `
      astrStyleFlagStandard: metafield(namespace: "astr", key: "style_flag") {
        value
      }
      astrStyleFlagPlus: metafield(namespace: "astr", key: "style_flag_plus_sizes") {
        value
      }
      astrColorFamily: metafield(namespace: "astr", key: "color_family") {
        value
      }
      astrOnlineInventory: metafield(namespace: "astr", key: "online_inventory") {
        value
      }
      astrOnlineInventoryStyle: metafield(namespace: "astr", key: "online_inventory_style") {
        value
      }
    `;
  }

  if (options.get_inventory) {
    variant_metafields_query += `
      astrOnlineInventory: metafield(namespace: "astr", key: "online_inventory") {
        value
      }
      astrOnlineInventoryStyle: metafield(namespace: "astr", key: "online_inventory_style") {
        value
      }
    `;
  }

  let nodeFields = `
    id
    title
    sku
    availableForSale
    quantityAvailable
    image {
      url
      altText
    }
    price {
      amount
      currencyCode
    }
    compareAtPrice {
      amount
      currencyCode
    }
    selectedOptions {
      name
      value
    }
  `;
  if (window.Shopify.shop.includes('staging')) {
    nodeFields = nodeFields.replace(/\s+quantityAvailable\s+/, '\n');
  }

  return `
    edges {
      node {
        id
        handle
        availableForSale
        title
        ${description_fields}
        ${collections_fields}
        ${collections_tags}
        onlineStoreUrl
        productType
        publishedAt
        featuredImage {
          id
          url
          altText
        }
        images(first: 200) {
          edges {
            node {
              url
              altText
            }
          }
        }
        ${product_metafields_query}
        priceRange {
          maxVariantPrice {
            amount
            currencyCode
          }
          minVariantPrice {
            amount
            currencyCode
          }
        }
        compareAtPriceRange {
          maxVariantPrice {
            amount
            currencyCode
          }
        }
        variants(first: 150) {
          edges {
            cursor
            node {
              ${nodeFields}
              ${variant_metafields_query}
            }
          }
        }
      }
  }`;
};
